import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr, fmtDate, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, SectionCard, StatusBadge } from "@/components/Bits";
import { UploadButton } from "@/components/Upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { PlusCircle, Check, X, Paperclip, Minus, Plus, CalendarClock, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Leaves() {
  const { user } = useAuth();
  const isReviewer = user?.role === "admin" || user?.role === "manager";
  const [leaves, setLeaves] = useState([]);
  const [balances, setBalances] = useState({});
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(isReviewer ? "review" : "mine");
  const [form, setForm] = useState({ leave_type: "", start_date: "", end_date: "", num_days: "", num_hours: "", reason: "", attachment_file_id: null });
  const [editId, setEditId] = useState(null);
  const [override, setOverride] = useState(false);
  const [holidays, setHolidays] = useState([]);

  const load = useCallback(() => {
    const scope = tab === "mine" ? "mine" : user?.role === "admin" ? "all" : "auto";
    api.get(`/leaves?scope=${scope}`).then((r) => setLeaves(r.data)).catch(() => {});
    api.get("/leaves/balances").then((r) => setBalances(r.data)).catch(() => {});
  }, [tab, user]);

  useEffect(() => { api.get("/meta/constants").then((r) => setTypes(r.data.leave_types)); }, []);
  useEffect(() => { api.get("/meta/holidays").then((r) => setHolidays(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const isOil = form.leave_type === "Off-in-lieu (OIL)";

  // Auto-calc working days (exclude weekends + SG holidays) once a date range + a non-OIL type are chosen
  const workingDays = useCallback((start, end) => {
    if (!start || !end || end < start) return 0;
    let count = 0;
    const cur = new Date(start + "T00:00:00");
    const last = new Date(end + "T00:00:00");
    const hset = new Set(holidays.map((h) => h.date));
    while (cur <= last) {
      const iso = cur.toISOString().slice(0, 10);
      const dow = cur.getDay();
      if (dow !== 0 && dow !== 6 && !hset.has(iso)) count++;
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  }, [holidays]);

  // Public holidays that fall on a weekday inside the chosen range (excluded from the leave count)
  const overlapHolidays = (!isOil && form.start_date && form.end_date && form.end_date >= form.start_date)
    ? holidays.filter((h) => {
        if (h.date < form.start_date || h.date > form.end_date) return false;
        const dow = new Date(h.date + "T00:00:00").getDay();
        return dow !== 0 && dow !== 6;
      })
    : [];

  useEffect(() => {
    if (!isOil && form.leave_type && form.start_date && form.end_date && form.end_date >= form.start_date) {
      setForm((f) => ({ ...f, num_days: String(workingDays(f.start_date, f.end_date)) }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.leave_type, form.start_date, form.end_date, isOil, workingDays]);

  const stepQty = (field, delta) => setForm((f) => {
    const v = Math.max(0, (parseFloat(f[field] || 0) + delta));
    return { ...f, [field]: String(Math.round(v * 2) / 2) };
  });

  const submit = async () => {
    try {
      const payload = { ...form, num_days: parseFloat(form.num_days || 0), num_hours: parseFloat(form.num_hours || 0) };
      if (editId) {
        await api.put(`/leaves/${editId}?override=${override}`, payload);
        toast.success("Leave request updated — sent for approval");
      } else {
        await api.post(`/leaves?override=${override}`, payload);
        toast.success("Leave application submitted");
      }
      setOpen(false); setEditId(null);
      setForm({ leave_type: "", start_date: "", end_date: "", num_days: "", num_hours: "", reason: "", attachment_file_id: null });
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const openCreate = () => {
    setEditId(null);
    setForm({ leave_type: "", start_date: "", end_date: "", num_days: "", num_hours: "", reason: "", attachment_file_id: null });
    setOverride(false);
    setOpen(true);
  };

  const openEdit = (l) => {
    setEditId(l.id);
    setForm({
      leave_type: l.leave_type, start_date: l.start_date, end_date: l.end_date,
      num_days: l.is_oil ? "" : String(l.num_days || ""), num_hours: l.is_oil ? String(l.num_days || "") : "",
      reason: l.reason || "", attachment_file_id: l.attachment_file_id || null,
    });
    setOverride(false);
    setOpen(true);
  };

  const remove = async (l) => {
    const label = l.status === "Approved" ? "Cancel this approved leave? The balance will be credited back." : "Withdraw this leave request?";
    if (!window.confirm(label)) return;
    try {
      await api.delete(`/leaves/${l.id}`);
      toast.success("Leave request removed");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const review = async (id, decision) => {
    try {
      await api.put(`/leaves/${id}/${decision}`, { comment: decision === "reject" ? "Reviewed" : "Approved" });
      toast.success(`Leave ${decision}d`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const shown = leaves;

  return (
    <div className="animate-fade-up">
      <PageTitle overline="Leave Management" title="Leave">
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditId(null); }}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="apply-leave-btn"><PlusCircle className="h-4 w-4 mr-1.5" /> Apply for Leave</Button>
          </DialogTrigger>
          <DialogContent className="bg-popover max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Leave Request" : "Apply for Leave"}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs text-muted-foreground">Step 1 · Select dates</Label>
                <div className="grid grid-cols-2 gap-3 mt-1.5">
                  <div><Label>Start Date</Label><Input type="date" data-testid="leave-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5" /></div>
                  <div><Label>End Date</Label><Input type="date" data-testid="leave-end" value={form.end_date} min={form.start_date || undefined} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5" /></div>
                </div>
              </div>
              {overlapHolidays.length > 0 && (
                <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2.5 text-sm" data-testid="leave-holiday-warning">
                  <p className="font-medium text-warning flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> {overlapHolidays.length} public holiday{overlapHolidays.length > 1 ? "s" : ""} in this range</p>
                  <p className="text-xs text-muted-foreground mt-1">{overlapHolidays.map((h) => `${fmtDate(h.date)} (${h.name})`).join(", ")} — these are already excluded from your leave days, so you won't spend leave on them.</p>
                </div>
              )}
              <div>
                <Label className="text-xs text-muted-foreground">Step 2 · Leave type</Label>
                <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                  <SelectTrigger data-testid="leave-type-select" className="mt-1.5" disabled={!form.start_date || !form.end_date}><SelectValue placeholder={!form.start_date || !form.end_date ? "Pick dates first" : "Select leave type"} /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    {types.map((t) => {
                      const b = balances[t];
                      const bal = b ? (t === "Off-in-lieu (OIL)" ? `${b.remaining}h` : `${b.remaining}d left`) : "";
                      return <SelectItem key={t} value={t}>{t} {bal && <span className="text-muted-foreground">· {bal}</span>}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              {form.leave_type && (
                <div>
                  <Label className="text-xs text-muted-foreground">Step 3 · {isOil ? "Number of hours" : "Number of days"}</Label>
                  {isOil ? (
                    <div className="flex items-center gap-2 mt-1.5">
                      <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => stepQty("num_hours", -0.5)} data-testid="hours-minus"><Minus className="h-4 w-4" /></Button>
                      <Input type="number" step="0.5" min="0" data-testid="leave-hours" value={form.num_hours} onChange={(e) => setForm({ ...form, num_hours: e.target.value })} className="text-center" placeholder="e.g. 4" />
                      <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => stepQty("num_hours", 0.5)} data-testid="hours-plus"><Plus className="h-4 w-4" /></Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mt-1.5">
                        <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => stepQty("num_days", -0.5)} data-testid="days-minus"><Minus className="h-4 w-4" /></Button>
                        <Input type="number" step="0.5" min="0" data-testid="leave-days" value={form.num_days} onChange={(e) => setForm({ ...form, num_days: e.target.value })} className="text-center" placeholder="e.g. 1.5" />
                        <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => stepQty("num_days", 0.5)} data-testid="days-plus"><Plus className="h-4 w-4" /></Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1.5">Auto-filled from working days (excludes weekends & public holidays). Adjust in 0.5-day steps for half-days.</p>
                    </>
                  )}
                </div>
              )}
              {form.leave_type && form.leave_type !== "Unpaid Leave" && (() => {
                const bal = balances[form.leave_type] || {};
                const qty = isOil ? parseFloat(form.num_hours || 0) : parseFloat(form.num_days || 0);
                const remaining = bal.remaining ?? 0;
                const after = Math.round((remaining - qty) * 100) / 100;
                const unit = isOil ? "h" : "d";
                const neg = after < 0;
                return (
                  <div className={`rounded-md border p-3 text-sm ${neg ? "border-destructive/40 bg-destructive/5" : "border-border bg-muted/40"}`} data-testid="leave-forecast">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Current balance</span>
                      <span className="font-mono-num">{remaining}{unit}</span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-muted-foreground">After this request</span>
                      <span className={`font-mono-num font-semibold ${neg ? "text-destructive" : "text-foreground"}`} data-testid="forecast-after">{after}{unit}</span>
                    </div>
                    {neg && <p className="text-xs text-destructive mt-1.5">This exceeds your available balance{user?.role === "admin" ? " — tick 'Override balance' to proceed." : "."}</p>}
                  </div>
                );
              })()}
              <div><Label>Reason</Label><Textarea data-testid="leave-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5" placeholder="Provide a brief reason" /></div>
              <div className="flex items-center justify-between">
                <UploadButton label={form.attachment_file_id ? "Attached" : "Attach (e.g. MC)"} done={!!form.attachment_file_id} onUploaded={(f) => setForm({ ...form, attachment_file_id: f.id })} />
                {user?.role === "admin" && (
                  <label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} data-testid="override-check" /> Override balance
                  </label>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} disabled={!form.leave_type || !form.start_date || !form.end_date || (isOil ? !(parseFloat(form.num_hours) > 0) : !(parseFloat(form.num_days) > 0))} data-testid="submit-leave">{editId ? "Save Changes" : "Submit Application"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageTitle>

      {/* Balance chips */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        {Object.entries(balances).map(([t, b]) => (
          <Card key={t} className="p-4 rounded-md border-border">
            <p className="text-xs text-muted-foreground truncate">{t}</p>
            <p className="font-heading font-extrabold text-2xl font-mono-num mt-1">
              {b.remaining}<span className="text-sm text-muted-foreground font-normal">{t === "Off-in-lieu (OIL)" ? "h" : "d"}</span>
            </p>
          </Card>
        ))}
      </div>

      <SectionCard
        title="Leave Applications"
        action={
          isReviewer && (
            <div className="flex gap-1 rounded-md border border-border p-0.5">
              <button onClick={() => setTab("review")} className={`px-3 py-1 text-sm rounded ${tab === "review" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="tab-review">Team</button>
              <button onClick={() => setTab("mine")} className={`px-3 py-1 text-sm rounded ${tab === "mine" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`} data-testid="tab-mine">Mine</button>
            </div>
          )
        }
      >
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {tab !== "mine" && <TableHead>Employee</TableHead>}
                <TableHead>Type</TableHead>
                <TableHead>Period</TableHead>
                <TableHead className="text-right">Qty</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.length === 0 && <TableRow><TableCell colSpan={tab !== "mine" ? 7 : 6} className="text-center text-muted-foreground py-8">No leave applications.</TableCell></TableRow>}
              {shown.map((l) => {
                const mine = tab === "mine";
                const canReview = isReviewer && !mine && l.status === "Pending";
                const canModify = mine || isReviewer; // employees: own; reviewers: any
                return (
                <TableRow key={l.id} data-testid={`leave-row-${l.id}`}>
                  {tab !== "mine" && <TableCell className="font-medium">{l.employee_name}</TableCell>}
                  <TableCell>{l.leave_type}</TableCell>
                  <TableCell className="text-sm">{fmtDate(l.start_date)} → {fmtDate(l.end_date)}</TableCell>
                  <TableCell className="text-right font-mono-num">{l.num_days}{l.is_oil ? "h" : "d"}</TableCell>
                  <TableCell className="max-w-[180px] truncate text-sm text-muted-foreground">
                    {l.attachment_file_id && <a href={fileUrl(l.attachment_file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center text-primary mr-1"><Paperclip className="h-3 w-3" /></a>}
                    {l.reason || "—"}
                  </TableCell>
                  <TableCell><StatusBadge status={l.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end items-center">
                      {canReview && (
                        <>
                          <Button size="icon" variant="outline" className="h-8 w-8 text-success" onClick={() => review(l.id, "approve")} data-testid={`approve-${l.id}`}><Check className="h-4 w-4" /></Button>
                          <Button size="icon" variant="outline" className="h-8 w-8 text-destructive" onClick={() => review(l.id, "reject")} data-testid={`reject-${l.id}`}><X className="h-4 w-4" /></Button>
                        </>
                      )}
                      {canModify && (
                        <>
                          <Button size="icon" variant="ghost" className="h-8 w-8" title="Edit" onClick={() => openEdit(l)} data-testid={`edit-leave-${l.id}`}><Pencil className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" title={l.status === "Approved" ? "Cancel leave" : "Withdraw"} onClick={() => remove(l)} data-testid={`withdraw-leave-${l.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </>
                      )}
                      {!canReview && !canModify && <span className="text-xs text-muted-foreground">{l.reviewed_by || "—"}</span>}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </SectionCard>
    </div>
  );
}
